import { connect } from 'react-redux';
import { useState, useEffect, } from 'react';

import { siteboardAction } from '../actions/defaultActions';
import { SET_USER_ROLE_INFO_DIALOGUE_SHOWING, SiteboardApiValidator, ADD_PROJECT_MEMBER, } from '../actions/types';
import { keyCountInObject, } from '../Util';

import MemberRolePicker from './MemberRolePicker';
import SortableTable from './SortableTable';
import PushButton from './PushButton';
import CollapsibleContainer from './CollapsibleContainer';
import AddProjectMember from './AddProjectMember';

// icon imports
import { UsersIcon, Plus, Info, Trash, Clock, ArrowUpRight, } from 'lucide-react';
import CircleXIcon from '../icons/circleXIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';
import InfoIcon from '../icons/infoIcon';

// test states
const testScrollMembers = false;
const testEmptyMembersState = false;

const ProjectMembers = ({ _users, _userId, _subscriptions, siteboardAction, project }) => {

  const memberCountInSelectedProject = !project  ? 0 : (keyCountInObject(project.members) + keyCountInObject(project.pendingMembers));

  const [isInviteExpanded, setIsInviteExpanded] = useState(memberCountInSelectedProject === 0);

  const projectMembers = [];
  const pendingProjectMembers = [];
  const selectedProject = project;
  const selectedSubscription = project ? _subscriptions[project.subscriptionId] : undefined;
  const currentAndPendingMembers = [];
  if(memberCountInSelectedProject > 0) {
    for(const memberId in project.members) {
      for(let i = 0; i < (testScrollMembers ? 20 : 1) && !testEmptyMembersState; i++) {
        projectMembers.push({
          id: memberId,
          ...project.members[memberId],
          ..._users[memberId],
          pending: false,
        });
      }
    }
    for(const email in project.pendingMembers) {
      pendingProjectMembers.push({
        ...project.pendingMembers[email],
        email: email,
        pending: true,
      });
    }
    projectMembers.sort((one, two) => {
      try {
        return _users[one.id].lastName.toLowerCase().localeCompare(_users[two.id].lastName.toLowerCase());
      } catch(error) {}
      return one.createdTimestamp - two.createdTimestamp;
    });

    currentAndPendingMembers.push(...projectMembers, ...pendingProjectMembers);
    currentAndPendingMembers.sort((one, two) => {
      try {
        return _users[one.id].lastName.toLowerCase().localeCompare(_users[two.id].lastName.toLowerCase());
      } catch(error) {}
      return one.createdTimestamp - two.createdTimestamp;
    });
  }

  useEffect(() => {
    const memberCountInSelectedProject = !project ? 0 : (keyCountInObject(project.members) + keyCountInObject(project.pendingMembers));
    if(memberCountInSelectedProject === 0) {
      setIsInviteExpanded(true);
    }
  }, [project]);

  const getUserAvatar = (user) => {
    return (
      <div className='relative flex justify-center items-center uppercase bg-[#f99917] rounded-full p-1 w-9 h-9 min-h-9 min-w-9 text-center text-black font-semibold'>
        <div>{user.firstName[0]}{user.lastName[0]}</div>
        <div className={`absolute bottom-0 translate-y-[60%] ${user.isOnline === true ? 'text-green-400' : 'text-red-400'} text-[0.7em] py-[2px] px-1 bg-[#444] rounded font-bold`}>
          {user.isOnline ? 'Online' : 'Offline'}
        </div>
      </div>
    );
  };

  const getAddUserButtonJSX = () => {
    if(!SiteboardApiValidator[ADD_PROJECT_MEMBER].isUserIdAuthorized(_userId, selectedSubscription, project)) return <></>;
    return (
      <PushButton
        textColor="white"
        bgColor="#471cff"
        hoverBgColor="#603aff"
        onClick={() => {
          setIsInviteExpanded(true);
        }}
      >
        <div className="flex items-center justify-center gap-2 py-1">
          <Plus size={18} />
          Add Team Member
        </div>
      </PushButton>
    );
  };

  return (
    <div className='flex flex-col gap-y-2'>

      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <div className="flex items-center gap-2">
            <div className="
              flex h-10 w-10 items-center justify-center
              rounded-lg text-[#f9a928]
            ">
              <UsersIcon size={'2.6em'} strokeWidth={1.8} />
            </div>

            <div className="flex flex-col">
              <h3 className="text-base font-bold tracking-tight">
                Project Team Members ({memberCountInSelectedProject})
              </h3>
              <p className="text-sm text-white/70">
                Manage people collaborating on this project
              </p>
            </div>
          </div>
        </div>

        {/* Wide layout - button to show invite new member component */}
        <div>
          <div className={`hidden md:block transition-opacity duration-200 ${isInviteExpanded ? 'opacity-0' : 'opacity-100'}`}>{getAddUserButtonJSX()} </div>
        </div>
      </div>

      {/* Narrow layout - button to show invite new member component */}
      <div className={`md:hidden py-2`}>
        <CollapsibleContainer isOpen={!isInviteExpanded}>
          {getAddUserButtonJSX()}
        </CollapsibleContainer>
      </div>
      
      {/* Invite new member by email */}
      <CollapsibleContainer isOpen={isInviteExpanded}>
        <AddProjectMember project={selectedProject} onClose={memberCountInSelectedProject === 0 ? null : () => {setIsInviteExpanded(false);}} />
      </CollapsibleContainer>

      {/* Narrow layout - list of project members */}
      <div className='flex flex-col gap-3 md:hidden'>
        {projectMembers.map((member) => (
          member.id in _users && (
            <div
              key={member.id}
              className='rounded-xl border border-white/10 bg-[#252525] p-4 text-white shadow-sm transition-colors duration-200 hover:border-white/20 motion-reduce:transition-none'
            >
              <div className='flex items-center gap-3'>
                <div className='shrink-0'>
                  {getUserAvatar(_users[member.id])}
                </div>

                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <div className='min-w-0 truncate text-sm font-semibold leading-5'>
                      {_users[member.id].firstName} {_users[member.id].lastName}
                    </div>

                    {_userId === member.id && (
                      <span className='shrink-0 rounded-full bg-[#f9a928]/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#f9a928]'>
                        You
                      </span>
                    )}
                  </div>

                  <div
                    className='mt-0.5 truncate text-xs leading-5 text-white/55'
                    title={_users[member.id].email}
                  >
                    {_users[member.id].email}
                  </div>
                </div>
              </div>

              <div className='mt-4 flex items-center justify-between gap-x-4 gap-y-2 pt-0'>
                <div className='text-sm font-medium text-white/55'>
                  Project role
                </div>

                <div className='grow h-[2.2em]'>
                  <MemberRolePicker
                    projectId={project.id}
                    memberId={member.id}
                  />
                </div>
              </div>
            </div>
          )
        ))}

        {pendingProjectMembers.map((pendingMember) => (
          <div
            key={pendingMember.email}
            className='rounded-xl border border-[#f9a928]/15 bg-[#252525] p-4 text-white shadow-sm transition-colors duration-200 hover:border-[#f9a928]/30 motion-reduce:transition-none'
          >
            <div className='flex items-start gap-3'>
              <div className='flex shrink-0 items-center justify-center pt-0.5'>
                <Clock size='2.2em' className='text-white' />
              </div>

              <div className='min-w-0 flex-1'>
                <div className='text-sm font-semibold leading-5'>
                  Pending Invite
                </div>

                <div
                  className='mt-0.5 break-all text-xs leading-5 text-white/55'
                  title={pendingMember.email}
                >
                  {pendingMember.email}
                </div>
              </div>
            </div>

            <div className='mt-4 flex items-center justify-between gap-x-4 gap-y-2 pt-0'>
              <div className='text-sm font-medium text-white/55'>
                Project role
              </div>

              <div className='grow h-[2.2em]'>
                <MemberRolePicker
                  projectId={project.id}
                  pendingMemberEmail={pendingMember.email}
                />
              </div>
            </div>

          </div>
        ))}
      </div>

      {/* Wide layout - list of project members */}
      <div className='hidden md:block'>
        <SortableTable 
          columns={[
            { 
              key: 'lastName', 
              label: "Last Name", 
              width:'22%', 
              render: (value, row) => 
                row.pending !== true
                  ? <div>
                      {value}
                      {_userId === row.id && 
                        <div className='inline font-bold text-[#f9a928]'>
                          <span className='shrink-0 rounded-full bg-[#f9a928]/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#f9a928] ml-1.5'>You</span>
                        </div>
                      }
                    </div> 
                  : <div className='flex items-center gap-1.5'>
                      <Clock size='1.4em' className='text-white' />
                      Pending Invite
                    </div>,
              sortValue: (value, row) => row.pending !== true ? value : '',
            },
            { 
              key: 'firstName', 
              label: "First Name", 
              width:'22%', 
              render: (value, row) => row.pending !== true ? value : '-', 
              sortValue: (value, row) => row.pending !== true ? value : '',
            },
            { 
              key: 'email', 
              label: "Email", 
              width:'39%', 
              render: (value) => value, 
            },
            { 
              key: 'role', 
              label: "Role", 
              width:'12%', 
              render: (value, row) => 
                row.pending !== true 
                  ? <div className='h-[2.2em]'><MemberRolePicker projectId={selectedProject.id} memberId={row.id} /></div>
                  : <div className='h-[2.2em]'><MemberRolePicker projectId={selectedProject.id} pendingMemberEmail={row.email} /></div>,
            },
            { 
              key: 'id', // TODO: need to modify SortableTable so it doesn't require a key to sort this column if sortValue is defined
              label: "Online", 
              width:'5%', 
              render: (value, row) => 
                row.pending !== true 
                  ? <div className='flex justify-center'>{_users[row.id].isOnline === true ? <CircleCheckIcon color='#00bc7d' /> : <CircleXIcon color='#f12035' />}</div>
                  : <div className='flex justify-center'><CircleXIcon color='#f12035' /></div>,
              sortValue: (value, row) => row.pending !== true && _users[row.id].isOnline === true ? 'true' : 'false',
            },
          ]}
          data={currentAndPendingMembers}
        />
      </div>

      {/* About user roles button */}
      <div className='flex justify-between mt-4'>
        <PushButton
          isInline={true}
          title="Learn about user roles and permissions"
          onClick={() => { siteboardAction(SET_USER_ROLE_INFO_DIALOGUE_SHOWING, { isOpen: true })}}
          textColor="white"
          bgColor="bg-neutral-700"
          hoverBgColor="bg-neutral-600"
        >
          <div className='flex items-center justify-between gap-2'>
            <span className='text-white/80'>About User Roles</span>
            <ArrowUpRight />
          </div>
        </PushButton>
      </div>
    </div>
  );
}

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, {siteboardAction})(ProjectMembers);
